import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalProps
} from '@chakra-ui/react';
import Swal from 'sweetalert2';
import { useQueryClient } from '@tanstack/react-query';
import { BsHddStack } from 'react-icons/bs';
import { FiSave } from 'react-icons/fi';
import { twMerge } from 'tailwind-merge';

import { Button, Input, Label, Select } from 'common/materials';
import { parseResponseError } from 'common/helpers';
import { fguiPortSettingQueryKey, useFguiAutoConnect, useLoader, useStore } from 'common/hooks';
import {
    getActiveProfiles,
    getUserSetting,
    saveUserSetting
} from 'data/fguiPort/datasources/fguiPort.datasource';
import {
    FguiPortMode,
    FguiPortProfile,
    FguiPortType
} from 'data/fguiPort/schemas/fguiPort.schema';

type Props = Pick<ModalProps, 'isOpen' | 'onClose'>;

type RadioCardProps = {
    checked: boolean;
    disabled?: boolean;
    title: string;
    description: string;
    onSelect: () => void;
};

function RadioCard({ checked, disabled, title, description, onSelect }: RadioCardProps) {
    return (
        <label
            className={twMerge(
                'flex cursor-pointer items-center gap-4 rounded-lg border p-4 transition-colors',
                checked ? 'border-blue-300 bg-blue-50' : 'border-neutral-200 bg-neutral-50',
                disabled && 'cursor-not-allowed opacity-60'
            )}
        >
            <input
                type="radio"
                className="h-5 w-5 accent-blue-600"
                checked={checked}
                disabled={disabled}
                onChange={onSelect}
            />
            <BsHddStack
                size={26}
                className="shrink-0 text-neutral-500"
            />
            <div className="flex flex-col">
                <span className="text-sm font-semibold">{title}</span>
                <span className="text-xs text-neutral-500">{description}</span>
            </div>
        </label>
    );
}

const toOption = (profile: FguiPortProfile) => ({ label: profile.profile_name, value: profile.profile_id });

function SettingPortModal({ isOpen, onClose }: Props) {
    const loader = useLoader();
    const queryClient = useQueryClient();
    const { userCompanyStore } = useStore();
    const { connect, getSelectedFguiItem } = useFguiAutoConnect();

    const [profiles, setProfiles] = useState<FguiPortProfile[]>([]);
    const [mode, setMode] = useState<FguiPortMode>('manual');
    const [portType, setPortType] = useState<FguiPortType>('standard');
    const [standardId, setStandardId] = useState<string>();
    const [otherId, setOtherId] = useState<string>();

    const standardOptions = useMemo(
        () => profiles.filter(profile => profile.port_type === 'standard').map(toOption),
        [profiles]
    );
    const otherOptions = useMemo(
        () => profiles.filter(profile => profile.port_type === 'other').map(toOption),
        [profiles]
    );

    // "เซิร์ฟเวอร์ปัจจุบัน" = the company selected in the FGUI side menu group, or "-"
    const selectedItem = getSelectedFguiItem();
    const isAuto = mode === 'auto';

    useEffect(() => {
        if (!isOpen) return;

        const load = async () => {
            try {
                loader.show();
                const [profileResponse, settingResponse] = await Promise.all([
                    getActiveProfiles(),
                    getUserSetting()
                ]);
                const activeProfiles = profileResponse.data.result_list ?? [];
                const setting = settingResponse.data;
                const current = activeProfiles.find(p => p.profile_id === setting.profile_id);

                setProfiles(activeProfiles);
                setMode(setting.mode === 'auto' && current ? 'auto' : 'manual');
                setPortType(current?.port_type ?? 'standard');
                setStandardId(
                    current?.port_type === 'standard'
                        ? current.profile_id
                        : activeProfiles.find(p => p.port_type === 'standard')?.profile_id
                );
                setOtherId(
                    current?.port_type === 'other'
                        ? current.profile_id
                        : activeProfiles.find(p => p.port_type === 'other')?.profile_id
                );
            } catch (error) {
                Swal.fire('Error', parseResponseError(error)?.message ?? (error as Error).message, 'error');
            } finally {
                loader.hide();
            }
        };

        load();
    }, [isOpen]);

    const handleSave = async () => {
        const profileId = portType === 'standard' ? standardId : otherId;
        if (isAuto && !profileId) {
            Swal.fire('แจ้งเตือน', 'กรุณาเลือกพอร์ตที่ต้องการใช้งาน', 'warning');
            return;
        }

        try {
            loader.show();
            const saved = { mode, profile_id: isAuto ? profileId! : null };
            await saveUserSetting(saved);

            // Menu clicks read the cached setting - update it so the change applies right away
            queryClient.setQueryData(fguiPortSettingQueryKey(userCompanyStore.citizenId), {
                citizen_id: userCompanyStore.citizenId,
                ...saved
            });
        } catch (error) {
            loader.hide();
            Swal.fire('Error', parseResponseError(error)?.message ?? (error as Error).message, 'error');
            return;
        }
        loader.hide();
        onClose();

        // Connect right away only when a company is selected (otherwise just saved)
        if (isAuto && selectedItem) {
            await connect(selectedItem);
        }
    };

    return (
        <Modal
            size="xl"
            isOpen={isOpen}
            onClose={onClose}
            isCentered
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader className="flex items-center gap-3">
                    <BsHddStack
                        size={32}
                        className="text-neutral-500"
                    />
                    <div className="flex flex-col">
                        <span className="text-xl font-bold">ตั้งค่าพอร์ตเชื่อมต่อ</span>
                        <span className="text-sm font-normal text-neutral-500">
                            ระบุพอร์ตของเซิร์ฟเวอร์ที่ต้องการเข้าใช้งาน
                        </span>
                    </div>
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <RadioCard
                            checked={mode === 'manual'}
                            title="ใช้งานแบบ Manual"
                            description="ไม่มีการเปลี่ยนแปลงการทำงานเดิม เลือกเมนูที่ต้องการเข้าใช้งานเอง"
                            onSelect={() => setMode('manual')}
                        />
                        <RadioCard
                            checked={isAuto}
                            disabled={profiles.length === 0}
                            title="เลือกพอร์ตอัตโนมัติที่ต้องการใช้งาน"
                            description="ใช้พอร์ตตามค่ามาตรฐาน หรือเลือกพอร์ตอัตโนมัติที่ระบบกำหนด"
                            onSelect={() => setMode('auto')}
                        />
                        {profiles.length === 0 && (
                            <span className="text-xs text-neutral-500">
                                ยังไม่มีพอร์ตให้เลือก กรุณาติดต่อผู้ดูแลระบบ
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col gap-2 border-t pt-4">
                        <Label>เซิร์ฟเวอร์ปัจจุบัน</Label>
                        <Input
                            readOnly
                            disabled
                            value={selectedItem?.group_name ?? '-'}
                        />
                    </div>

                    <div
                        className={twMerge(
                            'flex flex-col gap-3 rounded-lg p-3',
                            !isAuto && 'pointer-events-none bg-neutral-100 opacity-60'
                        )}
                    >
                        <Label>เลือกพอร์ตที่ต้องการใช้งาน</Label>
                        {(
                            [
                                ['standard', 'พอร์ตมาตรฐาน (แนะนำ)', 'ใช้พอร์ตตามค่ามาตรฐานที่องค์กรกำหนด', standardOptions, standardId, setStandardId],
                                ['other', 'พอร์ตอื่น ๆ', 'ระบุพอร์ตเอง', otherOptions, otherId, setOtherId]
                            ] as const
                        ).map(([type, title, description, options, value, setValue]) => (
                            <div
                                key={type}
                                className="flex flex-wrap items-center justify-between gap-3"
                            >
                                <label className="flex cursor-pointer items-center gap-3">
                                    <input
                                        type="radio"
                                        className="h-4 w-4 accent-blue-600"
                                        checked={portType === type}
                                        disabled={!isAuto || options.length === 0}
                                        onChange={() => setPortType(type)}
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-sm font-semibold">{title}</span>
                                        <span className="text-xs text-neutral-500">{description}</span>
                                    </div>
                                </label>
                                <div className="w-full sm:w-[200px]">
                                    <Select
                                        isDisabled={!isAuto || options.length === 0}
                                        placeholder="-"
                                        options={options}
                                        value={options.find(option => option.value === value) ?? null}
                                        onChange={option => {
                                            if (!option) return;
                                            setValue(option.value);
                                            setPortType(type);
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </ModalBody>

                <ModalFooter className="gap-2">
                    <Button
                        outline
                        onClick={onClose}
                    >
                        ยกเลิก
                    </Button>
                    <Button
                        className="gap-2 bg-blue-600 text-white"
                        onClick={handleSave}
                    >
                        <FiSave />
                        บันทึกและเชื่อมต่อ
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default observer(SettingPortModal);
